import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import { Download, Edit3, ChevronDown, FileText } from 'lucide-react';
import { toast } from '../../../../ui/toast';
import {
  BriefcaseBusinessIcon,
  ChartIcons,
  ClipBoardIcons,
  DashboardIcons,
  LightningIcons,
  NotebookIcons,
} from '../../../../icons/icons';
import { NameEmailAvatar } from '../../../../ui';
import {
  createApplicantNote,
  deleteApplicantNote,
} from '../../../../utils/api_service/JobOpenings';
import { Applicant } from '../../../../utils/types/JobOpenings';
import {
  AddNoteDialog,
  DocumentsTab,
  EducationTab,
  ExperienceTab,
  NotesTab,
  OverviewTab,
  PreQualResultsTab,
  SkillsTab,
} from './Components';
import { useCandidate } from './Hooks/UseCandidate';
import { PDFDisplay } from './PDFDisplay';
import { CCAResultsTab } from './Tabs/CCAResultsTab';

export interface ICandidateProfile {
  selectedCandidate: Applicant;
  handleSendEmailToSingleApplicant?: (
    applicant: Applicant,
    updateStatusFirst?: boolean,
  ) => Promise<void>;
}

export const CandidateProfile: React.FC<ICandidateProfile> = ({
  selectedCandidate,
  handleSendEmailToSingleApplicant,
}) => {
  const {
    isOpen,
    applicant,
    loading,
    isHighlightHit,
    leftWidth,
    activeTab,
    containerRef,
    dropdownRef,
    handleButtonClick,
    handleOptionClick,
    options,
    setActiveTab,
    getJobApplicants,
    selectedDocument,
    setSelectedDocument,
    pdfUrl,
    docLoading,
    handleResendInvite,
    isResending,
    id,
  } = useCandidate({ selectedCandidate, handleSendEmailToSingleApplicant });

  // State for Add Note Dialog
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Compute matched skills and behavioral attributes based on isHighlightHit
  const matchedSkills = selectedCandidate?.matchedSkills || [];
  const fullName =
    selectedCandidate?.name ||
    `${selectedCandidate?.firstName || ''} ${
      selectedCandidate?.lastName || ''
    }`.trim() ||
    'Candidate';

  // Handle saving note
  const handleSaveNote = async (note: any) => {
    try {
      const response = await createApplicantNote(
        selectedCandidate.userId,
        note,
      );
      if (response.ok) {
        getJobApplicants();
      }
    } catch (e) {
      console.log(e);
    }
    setIsNoteDialogOpen(false);
  };

  const handleDelete = async (noteId: string) => {
    try {
      const response = await deleteApplicantNote(noteId);
      if (response.ok) {
        getJobApplicants();
        setIsModalOpen(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleDownloadCV = async () => {
    if (!applicant?.pdfUrl) return;

    let url = applicant.pdfUrl;
    if (!url.startsWith('http')) {
      url = `${window.location.origin}${url}`;
    }

    const toastId = toast.info('Preparing download...', { autoClose: false });

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const extension = url.split('?')[0].split('.').pop() || 'pdf';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Download failed:', error);
      toast.dismiss(toastId);

      // Fallback to standard download if fetch fails (e.g. CORS)
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      const extension = url.split('?')[0].split('.').pop() || 'pdf';
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.warn(
        'Download initiated via fallback. Checking extension compatibility.',
      );
    }
  };

  // Tab configuration for better maintainability
  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: <DashboardIcons /> },
    { id: 'skills' as const, label: 'Skills', icon: <LightningIcons /> },
    {
      id: 'experience' as const,
      label: 'Experience',
      icon: <BriefcaseBusinessIcon />,
    },
    { id: 'education' as const, label: 'Education', icon: <NotebookIcons /> },
    {
      id: 'prequal' as const,
      label: 'Pre-Qual',
      icon: <ClipBoardIcons />,
    },
    { id: 'notes' as const, label: 'Notes', icon: <NotebookIcons /> },
    { id: 'documents' as const, label: 'Documents', icon: <ClipBoardIcons /> },
    { id: 'cca' as const, label: 'CCA Results', icon: <ChartIcons /> },
  ];

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)',
        }}
      >
        {/* Left panel */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            bgcolor: '#ffffff',
            borderRight: '1px solid #e5e7eb',
            height: '100%',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              borderBottom: '1px solid #e5e7eb',
              background: 'linear-gradient(to right, #ffffff, #f9fafb)',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 1 }}>
              {/* Avatar and Move Button Row */}
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <NameEmailAvatar
                    avatar={
                      selectedCandidate.symbol &&
                      selectedCandidate.symbol !== 'image://null' &&
                      selectedCandidate.symbol !== 'image://undefined'
                        ? selectedCandidate.symbol.replace('image://', '')
                        : ''
                    }
                    email={applicant?.email ?? 'N/A'}
                    name={selectedCandidate.name}
                    applicantStatus={
                      applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                      applicant?.applicantStatus === 'Unsuccessful-Pending'
                        ? 'Unsuccessful'
                        : applicant?.applicantStatus ??
                          applicant?.status ??
                          'N/A'
                    }
                  />
                </Box>

                {/* Action Buttons */}
                <Stack direction="row" spacing={1} sx={{ pt: 1, pr: 1 }}>
                  {applicant?.pdfUrl && (
                    <Button
                      onClick={handleDownloadCV}
                      variant="contained"
                      size="small"
                      startIcon={<Download size={14} />}
                      sx={{
                        background: 'linear-gradient(to right, #16a34a, #059669)',
                        color: '#ffffff',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          background: 'linear-gradient(to right, #15803d, #047857)',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                        },
                      }}
                      title="Download CV/Resume"
                      aria-label="Download candidate CV or resume"
                    >
                      Download CV
                    </Button>
                  )}

                  {/* Add Note Button */}
                  <Button
                    onClick={() => setIsNoteDialogOpen(true)}
                    variant="contained"
                    size="small"
                    startIcon={<Edit3 size={14} />}
                    sx={{
                      background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                      color: '#ffffff',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        background: 'linear-gradient(to right, #1d4ed8, #4338ca)',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                      },
                    }}
                    aria-label="Add note about candidate"
                  >
                    Add Note
                  </Button>

                  {/* Move To Dropdown */}
                  <Box sx={{ position: 'relative' }} ref={dropdownRef}>
                    <Button
                      onClick={handleButtonClick}
                      variant="contained"
                      size="small"
                      endIcon={
                        <ChevronDown
                          size={14}
                          style={{
                            transform: isOpen ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                          }}
                        />
                      }
                      sx={{
                        background: 'linear-gradient(to right, #16a34a, #059669)',
                        color: '#ffffff',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          background: 'linear-gradient(to right, #15803d, #047857)',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
                        },
                      }}
                      aria-label="Move candidate to different status"
                      aria-expanded={isOpen}
                    >
                      Move to
                    </Button>

                    {isOpen && (
                      <Paper
                        elevation={8}
                        sx={{
                          position: 'absolute',
                          right: 0,
                          mt: 1,
                          width: 208,
                          zIndex: 30,
                          overflow: 'hidden',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {options.map((option, idx) => (
                          <Box
                            key={idx}
                            component="button"
                            onClick={() => handleOptionClick(option)}
                            sx={{
                              width: '100%',
                              textAlign: 'left',
                              px: 2,
                              py: 1.25,
                              fontSize: '0.875rem',
                              cursor: 'pointer',
                              color: '#374151',
                              bgcolor: '#ffffff',
                              border: 'none',
                              borderBottom: idx === options.length - 1 ? 'none' : '1px solid #f3f4f6',
                              transition: 'all 0.15s',
                              '&:hover': {
                                background: 'linear-gradient(to right, #f0fdf4, #ecfdf5)',
                                color: '#15803d',
                              },
                              '&:focus': { outline: 'none', bgcolor: '#f0fdf4' },
                            }}
                          >
                            {option}
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Tabs Bar */}
          <Box sx={{ flexShrink: 0, borderBottom: '1px solid #e5e7eb', bgcolor: '#ffffff' }}>
            <Box component="nav" sx={{ display: 'flex', gap: 3, px: 3 }} aria-label="Tabs">
              {tabs.map((tab) => (
                <Box
                  component="button"
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  sx={{
                    whiteSpace: 'nowrap',
                    py: 1,
                    px: 0.5,
                    border: 'none',
                    bgcolor: activeTab === tab.id ? 'rgba(240, 253, 244, 0.6)' : 'transparent',
                    borderBottom: activeTab === tab.id ? '2px solid #16a34a' : '2px solid transparent',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: activeTab === tab.id ? '#16a34a' : '#6b7280',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: activeTab === tab.id ? '#16a34a' : '#374151',
                      borderBottomColor: activeTab === tab.id ? '#16a34a' : '#d1d5db',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', transform: 'scale(0.85)' }}>{tab.icon}</Box>
                  {tab.label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Scrollable Content Area */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                width: `${leftWidth}%`,
              }}
            >
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <Box role="tabpanel" id="overview-panel" aria-labelledby="overview-tab">
                    <OverviewTab
                      address={applicant?.contactInfo?.address}
                      applicantStatus={
                        applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                        applicant?.applicantStatus === 'Unsuccessful-Pending'
                          ? 'Unsuccessful'
                          : applicant?.applicantStatus ??
                            applicant?.status ??
                            'N/A'
                      }
                      email={applicant?.email ?? 'N/A'}
                      phone={applicant?.contactInfo?.phone ?? 'N/A'}
                      socials={applicant?.socials}
                      notes={(applicant as any)?.notes}
                      aiScore={(applicant as any)?.aiScore}
                      statusHistory={applicant?.statusHistory || (selectedCandidate as any)?.statusHistory}
                      createdAt={applicant?.createdAt || (selectedCandidate as any)?.createdAt}
                    />
                  </Box>
                )}

                {/* Skills Tab */}
                {activeTab === 'skills' && (
                  <Box role="tabpanel" id="skills-panel" aria-labelledby="skills-tab">
                    <SkillsTab
                      skills={applicant?.skills}
                      isHighlightHit={isHighlightHit}
                      matchedSkills={matchedSkills}
                      behavioralAttributes={applicant?.behavioralAttributes}
                      matchedBehavior={selectedCandidate.matchedBehavior}
                    />
                  </Box>
                )}

                {/* Experience Tab */}
                {activeTab === 'experience' && (
                  <Box role="tabpanel" id="experience-panel" aria-labelledby="experience-tab">
                    <ExperienceTab experience={applicant?.workExperience} />
                  </Box>
                )}

                {/* Education Tab */}
                {activeTab === 'education' && (
                  <Box role="tabpanel" id="education-panel" aria-labelledby="education-tab">
                    <EducationTab educations={applicant?.educations} />
                  </Box>
                )}

                {/* Pre-Qualifying Results Tab */}
                {activeTab === 'prequal' && (
                  <Box role="tabpanel" id="prequal-panel" aria-labelledby="prequal-tab">
                    <PreQualResultsTab
                      jobOpeningId={id!}
                      jobApplicantId={selectedCandidate.userId}
                      handleResendInvite={handleResendInvite}
                      isResending={isResending}
                      preQualSentAt={applicant?.preQualSentAt}
                    />
                  </Box>
                )}

                {/* Notes Tab */}
                {activeTab === 'notes' && (
                  <Box role="tabpanel" id="notes-panel" aria-labelledby="notes-tab">
                    <NotesTab
                      notes={(applicant as any)?.conversationNotes}
                      getJobApplicants={getJobApplicants}
                      onDelete={handleDelete}
                      isModalOpen={isModalOpen}
                      setIsModalOpen={setIsModalOpen}
                    />
                  </Box>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                  <Box role="tabpanel" id="documents-panel" aria-labelledby="documents-tab">
                    <DocumentsTab
                      documents={applicant?.applicantDocuments || []}
                      selectedDocumentId={selectedDocument?.id}
                      onDocumentSelect={setSelectedDocument}
                      pdfUrl={applicant?.pdfUrl}
                      baseFilename={applicant?.baseFilename}
                      uploadedAt={applicant?.createdAt}
                    />
                  </Box>
                )}

                {/* CCA Tab */}
                {activeTab === 'cca' && (
                  <Box role="tabpanel" id="cca-panel" aria-labelledby="cca-tab">
                    <CCAResultsTab candidate={selectedCandidate} />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Resizer */}
            <Box
              id="resizer"
              role="separator"
              aria-label="Resize panel separator"
              aria-orientation="vertical"
              sx={{
                width: 4,
                cursor: 'col-resize',
                background: 'linear-gradient(to bottom, #22c55e, #10b981)',
                height: '100%',
                flexShrink: 0,
                position: 'relative',
                zIndex: 20,
                transition: 'all 0.2s',
                '&:hover': {
                  width: 8,
                  boxShadow: 3,
                },
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 4,
                  bgcolor: '#ffffff',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.5 },
                }}
              />
            </Box>

            {/* Right panel: PDF viewer */}
            <Box
              sx={{
                background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                width: `${100 - leftWidth}%`,
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={56} sx={{ color: '#16a34a', mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 600 }}>
                      Loading Resume...
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                      Please wait while we fetch the document
                    </Typography>
                  </Box>
                </Box>
              ) : pdfUrl || docLoading ? (
                <Box sx={{ height: '100%' }}>
                  <PDFDisplay pdfUrl={pdfUrl} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <FileText size={56} color="#9ca3af" style={{ margin: '0 auto 16px' }} />
                    <Typography variant="subtitle1" sx={{ color: '#4b5563', fontWeight: 500 }}>
                      No Resume Available
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                      Resume not uploaded for this candidate
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add Note Dialog */}
      <AddNoteDialog
        isOpen={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        onSave={handleSaveNote}
        candidateName={selectedCandidate.name}
      />
    </>
  );
};

export default CandidateProfile;
